import { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { getLegalDoc, getLegalNeighbors, LEGAL_DOCS } from "../lib/legal-content";
import BrandName from "../components/site/BrandName";

const fade = (delay = 0, y = 18) => ({
  initial: { opacity: 0, y },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
});

/**
 * A masked line-by-line reveal for hero titles.
 * `text` is broken into individual words that rise from below a mask.
 */
function MaskedTitle({ text, testid }) {
  const words = text.split(" ");
  return (
    <h1
      data-testid={testid}
      className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tightest leading-[1.02] text-white"
    >
      <span className="block">
        {words.map((w, i) => (
          <span
            key={i}
            className="inline-block overflow-hidden align-baseline pb-[0.08em] mr-[0.22em] last:mr-0"
          >
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{
                duration: 0.9,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.12 + i * 0.09,
              }}
              className="inline-block"
            >
              {w}
            </motion.span>
          </span>
        ))}
      </span>
    </h1>
  );
}

function LegalHero({ doc, total }) {
  const { scrollY } = useScroll();
  // Subtle parallax on the oversized numeral.
  const numeralY = useTransform(scrollY, [0, 500], [0, -80]);
  const numeralOpacity = useTransform(scrollY, [0, 400], [1, 0.25]);

  return (
    <section
      className="relative isolate bg-[#0A0A0A] text-white overflow-hidden pt-24 md:pt-28 pb-12 md:pb-16"
      data-testid="legal-hero"
    >
      {/* Ambient orange glow */}
      <div
        aria-hidden="true"
        className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-orange-600/18 blur-[130px] pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-orange-600/10 blur-[130px] pointer-events-none"
      />

      {/* Oversized numeral — editorial serif, parallax */}
      <motion.div
        aria-hidden="true"
        style={{ y: numeralY, opacity: numeralOpacity }}
        className="pointer-events-none absolute right-[-3%] top-[8%] md:right-[-1%] md:top-[2%] select-none"
      >
        <span
          className="block font-serif italic text-white/[0.05] leading-none"
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: "clamp(240px, 42vw, 620px)",
            letterSpacing: "-0.06em",
          }}
        >
          {doc.docNumber}
        </span>
      </motion.div>

      <div className="container-x relative">
        <motion.div {...fade(0)} className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/55 font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse-soft" />
          Legal
          <span className="text-white/25">·</span>
          <span className="text-white/70">Doc {doc.docNumber} / 0{total}</span>
        </motion.div>

        <div className="mt-6 max-w-4xl">
          <MaskedTitle text={doc.title} testid="legal-title" />
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
            data-testid="legal-intro"
            className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-2xl"
          >
            {(() => {
              const escaped = doc.accentWord.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
              const rx = new RegExp("(" + escaped + ")", "i");
              const parts = doc.intro.split(rx);
              return parts.map((chunk, i) =>
                rx.test(chunk) ? (
                  <span
                    key={i}
                    className="text-orange-400"
                    style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic" }}
                  >
                    {chunk}
                  </span>
                ) : (
                  <span key={i}>{chunk}</span>
                )
              );
            })()}
          </motion.p>
        </div>
      </div>
    </section>
  );
}

function ChapterBlock({ index, chapter }) {
  const num = String(index + 1).padStart(2, "0");
  return (
    <motion.article
      {...fade(0.02 * index)}
      data-testid={`legal-chapter-${index}`}
      className="relative grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-9 border-t border-black/10 first:border-t-0"
    >
      {/* Numeral column */}
      <div className="col-span-12 md:col-span-3 lg:col-span-2">
        <div
          className="text-orange-600 select-none"
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 0.9,
            letterSpacing: "-0.04em",
          }}
        >
          {num}
        </div>
        <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-black/40 font-semibold">
          Chapter
        </div>
      </div>

      {/* Content column */}
      <div className="col-span-12 md:col-span-9 lg:col-span-10">
        <h2 className="font-display text-xl md:text-2xl font-bold tracking-tightest leading-[1.15] text-black">
          {chapter.heading}
        </h2>
        <div className="mt-3 space-y-2.5 text-[15px] md:text-base text-black/75 leading-relaxed max-w-3xl">
          {chapter.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
          {chapter.list && (
            <ul className="mt-1 space-y-1.5">
              {chapter.list.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="shrink-0 mt-[10px] w-1.5 h-1.5 rounded-full bg-orange-600" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function DocNavigator({ prev, next }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-10 md:mt-12">
      <Link
        to={`/${prev.slug}`}
        data-testid="legal-nav-prev"
        className="group flex items-center justify-between rounded-2xl border border-black/10 px-5 md:px-6 py-5 md:py-6 hover:border-orange-600/50 hover:bg-[#FAFAFA] transition-all duration-300"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="shrink-0 w-10 h-10 rounded-full border border-black/10 flex items-center justify-center group-hover:border-orange-600 group-hover:text-orange-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-black/40 font-semibold">
              Previous · Doc {prev.docNumber}
            </div>
            <div className="font-display mt-1 text-base md:text-lg font-semibold tracking-tight truncate">
              {prev.label}
            </div>
          </div>
        </div>
      </Link>
      <Link
        to={`/${next.slug}`}
        data-testid="legal-nav-next"
        className="group flex items-center justify-between rounded-2xl border border-black/10 px-5 md:px-6 py-5 md:py-6 hover:border-orange-600/50 hover:bg-[#FAFAFA] transition-all duration-300"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="min-w-0 text-right ml-auto">
            <div className="text-[10px] uppercase tracking-[0.22em] text-black/40 font-semibold">
              Next · Doc {next.docNumber}
            </div>
            <div className="font-display mt-1 text-base md:text-lg font-semibold tracking-tight truncate">
              {next.label}
            </div>
          </div>
          <div className="shrink-0 w-10 h-10 rounded-full border border-black/10 flex items-center justify-center group-hover:border-orange-600 group-hover:text-orange-600 transition-colors">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function LegalPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").split("/")[0];
  const doc = getLegalDoc(slug);

  // Ensure legal pages load at absolute top on every mount.
  useEffect(() => {
    const top0 = () => {
      try { window.scrollTo(0, 0); } catch (e) { /* noop */ }
      try {
        document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      } catch (e) { /* noop */ }
    };
    top0();
    requestAnimationFrame(top0);
    setTimeout(top0, 60);
  }, [slug]);

  if (!doc) return <Navigate to="/" replace />;

  const { prev, next, total } = getLegalNeighbors(slug);

  return (
    <div className="min-h-screen bg-white text-black font-sans">
      {/* Minimal sticky header — mirrors Community Guidelines pattern */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-black/5">
        <div className="container-x flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-black/70 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-display font-bold text-base md:text-lg tracking-tight text-black">
              <BrandName />
            </span>
          </Link>
          <div className="hidden sm:flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-black/45 font-semibold">
            <span>{doc.label}</span>
            <span className="text-black/20">·</span>
            <span>Doc {doc.docNumber} / 0{total}</span>
          </div>
        </div>
      </header>

      <LegalHero doc={doc} total={total} />

      {/* Body — numbered manifesto chapters */}
      <section className="py-10 md:py-14 bg-white text-black" data-testid="legal-body">
        <div className="container-x">
          <div className="max-w-6xl">
            {doc.chapters.map((chapter, i) => (
              <ChapterBlock key={i} index={i} chapter={chapter} />
            ))}
          </div>

          <DocNavigator prev={prev} next={next} />
        </div>
      </section>

      {/* Closing back-to-home */}
      <section className="pb-12 md:pb-16 bg-white text-black" data-testid="legal-closing">
        <div className="container-x">
          <motion.div
            {...fade(0)}
            className="relative max-w-4xl mx-auto rounded-3xl border border-black/10 bg-[#0A0A0A] text-white p-7 md:p-10 overflow-hidden"
          >
            <div
              aria-hidden="true"
              className="absolute -top-16 -right-12 w-72 h-72 rounded-full bg-orange-600/25 blur-3xl pointer-events-none"
            />
            <p className="relative text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">
              Have a question about this document? We&rsquo;d love to help — reach us any time through the Contact section.
            </p>
            <div className="relative mt-7 flex flex-wrap gap-3">
              <Link
                to="/"
                data-testid="legal-back-cta"
                className="inline-flex items-center gap-2 rounded-full bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to <BrandName /></span>
              </Link>
              <Link
                to="/#contact"
                data-testid="legal-contact-cta"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/45 text-white font-semibold px-5 py-2.5 text-sm transition-colors"
              >
                Go to Contact
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Minimal footer for legal pages */}
      <footer className="border-t border-black/5 bg-white py-8">
        <div className="container-x flex flex-col sm:flex-row items-center gap-3 sm:justify-between text-xs text-black/45">
          <div>© {new Date().getFullYear()} <BrandName />. All rights reserved.</div>
          <div className="flex items-center gap-4">
            {LEGAL_DOCS.filter((d) => d.slug !== slug).map((d) => (
              <Link
                key={d.slug}
                to={`/${d.slug}`}
                data-testid={`legal-footer-${d.slug}`}
                className="hover:text-black transition-colors"
              >
                {d.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
